import { Button, Message, Modal } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import React from 'react';
import { useHistory } from 'react-router';

import { ARRETES, MENUS, REGISSEUR } from '@/constants';
import { baseUrlFront, postArreteAutomatique } from '@/routes';
import { estEncaissementDirect } from '@/utils';
import { useRegieStore, useRegisseurStore } from '@/zustand';

// Persiste entre les remounts du composant (remount à chaque changement de page par le framework).
// Réinitialisé uniquement au reload complet de la page.
let lastHandledIdentifiant: number | null = null;

/**
 * ArreteAnnuelProcedureCreator
 *
 * Procédure d'arrêté annuel de rattrapage — Cas 1 (entrée en janvier alors qu'il reste des
 * encaissements/annulations datés jusqu'au 31/12 N-1 non rattachés à un arrêté périodique).
 *
 * Au premier accès de la régie (une fois par sélection) :
 *  1. déclenche le dernier arrêté journalier antidaté (POST /arretes/automatique, arreteAnnuel=true ;
 *     422 silencieux s'il n'y a rien à arrêter) ;
 *  2. ouvre une modale forte mais FERMABLE invitant à démarrer la clôture (périodique → annuel).
 *
 * La procédure est volontairement quittable : le vrai garde-fou est le blocage d'encaissement
 * côté serveur (encaissementPossible=false tant que l'annuel N-1 n'est pas créé). Exclue pour les
 * agents de guichet et en mode embedded (encaissement direct). Cas 2 est géré séparément par
 * ArreteAnnuelAutomatiqueCreator. Ne rend que la modale.
 */
const ArreteAnnuelProcedureCreator = () => {
  const regie = useRegieStore((state) => state.regie);
  const connectedRegisseur = useRegisseurStore((state) => state.regisseur);
  const { mandat } = connectedRegisseur || {};
  const isAgent = mandat === REGISSEUR.TYPE_AGENT;

  const history = useHistory();

  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const identifiant = regie?.identifiant;
    if (!identifiant) return;
    if (lastHandledIdentifiant === identifiant) return;
    // En mode embedded (encaissement direct), aucune procédure automatique.
    if (estEncaissementDirect()) return;
    // Les agents de guichet ne pilotent pas la clôture (ils restent bloqués pour encaisser).
    if (isAgent) return;
    // Cas 1 uniquement (le Cas 2 est géré par ArreteAnnuelAutomatiqueCreator).
    if (!regie?.arreteAnnuelRequis || regie?.arreteAnnuelCas !== 1) return;

    lastHandledIdentifiant = identifiant;

    // 1. Journalier de rattrapage antidaté à la fin d'exercice (silencieux ; 422 = rien à arrêter).
    request
      .post(postArreteAutomatique(), { type: ARRETES.TYPE_JOURNALIER, arreteAnnuel: true })
      .catch(() => {});

    // 2. Inviter à démarrer la clôture.
    setOpen(true);
  }, [regie?.identifiant, regie?.arreteAnnuelRequis, regie?.arreteAnnuelCas, isAgent]);

  const handleStart = React.useCallback(() => {
    setOpen(false);
    history.push(`${baseUrlFront}${MENUS.LOCATIONS[MENUS.MENU_ARRETE_PERIODIQUE]}`);
  }, [history]);

  if (!open) return null;

  return (
    <Modal open size="small" onClose={() => setOpen(false)} closeIcon>
      <Modal.Header>Clôture annuelle obligatoire</Modal.Header>
      <Modal.Content>
        <Message warning>
          <Message.Header>{`Vous devez clôturer l'exercice ${regie?.arreteAnnuelAnnee ?? ''}`}</Message.Header>
          <p>
            {
              "Des encaissements et/ou annulations datés jusqu'au 31 décembre n'ont pas encore été rattachés à un arrêté périodique. Vous devez procéder à l'arrêté périodique puis à l'arrêté annuel (antidatés au 31 décembre)."
            }
          </p>
          <p>
            <strong>
              {"Tant que la procédure n'est pas terminée, aucun encaissement n'est possible."}
            </strong>
          </p>
        </Message>
      </Modal.Content>
      <Modal.Actions>
        <Button content="Plus tard" onClick={() => setOpen(false)} basic />
        <Button content="Démarrer la clôture" onClick={handleStart} positive />
      </Modal.Actions>
    </Modal>
  );
};

export default ArreteAnnuelProcedureCreator;
